import React, { useMemo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import {
  TooltipComponent,
  GridComponent,
  LegendComponent
} from 'echarts/components';
import { BarChart, LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../providers/AppProvider';

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  BarChart,
  LineChart,
  CanvasRenderer
]);

// 🔵 paletă unică, stabilă pe light/dark
const COLORS = {
  planned: '#2563EB', // blue-600
  executed: '#22D3EE', // cyan-400
  bg: '#CBD5E1',
  bgDark: '#334155',
  text: '#0F172A',
  textDark: '#E5E7EB'
};

function sum(arr, key) {
  return arr.reduce((acc, o) => acc + Number(o?.[key] || 0), 0);
}

const getOptions = (isDark, jobs) => {
  const planned = sum(jobs, 'plannedLength') / 1000;
  const executed = sum(jobs, 'executedLength') / 1000; // acum 0

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: () => `
        <div style="font-weight:700;margin-bottom:6px;">
          Production Overview
        </div>
        <div>Planned: <b>${planned} km</b></div>
        <div>Executed: <b>${executed} km</b></div>
        <div>Remaining: <b>${planned - executed} km</b></div>
      `
    },
    legend: {
      top: 0,
      textStyle: {
        color: isDark ? COLORS.textDark : COLORS.text,
        fontWeight: 600
      }
    },
    xAxis: {
      type: 'category',
      data: ['Production'],
      axisLabel: {
        color: isDark ? COLORS.textDark : COLORS.text,
        fontWeight: 700
      },
      axisLine: {
        lineStyle: {
          color: isDark ? COLORS.bgDark : COLORS.bg
        }
      },
      axisTick: false
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: isDark ? COLORS.textDark : COLORS.text,
        formatter: v => `${v} km`
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false }
    },
    series: [
      {
        name: 'Planned Length',
        type: 'bar',
        barWidth: 26,
        itemStyle: {
          color: COLORS.planned,
          borderRadius: [6, 6, 0, 0]
        },
        data: [planned]
      },
      {
        name: 'Executed Length',
        type: 'line',
        symbol: 'circle',
        symbolSize: 10,
        lineStyle: {
          width: 4,
          color: COLORS.executed
        },
        itemStyle: {
          color: COLORS.executed
        },
        data: [executed]
      }
    ],
    grid: {
      left: 0,
      right: 0,
      bottom: 0,
      top: 40,
      containLabel: true
    },
    animation: false
  };
};

const ProductionJobLengthLine = ({ jobs = [], style }) => {
  const {
    config: { theme }
  } = useAppContext();

  const isDark = theme === 'dark';
  const safeJobs = Array.isArray(jobs) ? jobs : [];

  const option = useMemo(
    () => getOptions(isDark, safeJobs),
    [isDark, safeJobs]
  );

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      style={style || { height: 280, width: '100%' }}
    />
  );
};

export default ProductionJobLengthLine;
